import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  BOX_TYPE_OPTIONS,
  CUSTOMER_OPTIONS,
  emptyAccessoryTableRow,
  mockQuoteAccessories,
  mockQuoteBoxStructure,
  mockQuoteDetails,
  mockQuoteForm,
} from "../data";
import {
  Quote,
  QuoteAccessories,
  QuoteAccessoriesUpdate,
  QuoteAccessoryTableKey,
  QuoteBoxStructure,
  QuoteFrontWall,
  QuoteOptionItem,
  QuoteQuotationForm,
  QuoteStatus,
  QuoteStatusFilter,
  QuotesState,
} from "../types";
import { AddOrderForm } from "app/containers/CommonModal/types";
import { initialState } from "./initialState";

export const sliceKey = "quotesState";

const clone = <T>(value: T): T => structuredClone(value);

const ensureAccessories = (state: QuotesState): QuoteAccessories => {
  if (!state.accessories) {
    state.accessories = clone(mockQuoteAccessories);
  }

  return state.accessories;
};

const quotesSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getQuotesList(state) {
      state.loading = state.list.length === 0;
      state.error = null;
    },

    getQuotesListSuccess(state, action: PayloadAction<Quote[]>) {
      state.loading = false;
      state.list = action.payload;
    },

    getQuotesListFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getQuoteDetails(state, action: PayloadAction<string>) {
      state.detailLoading = false;
      state.error = null;

      const quote = state.list.find((item) => item.id === action.payload);

      if (quote) {
        state.selectedQuote = quote;
      }

      state.quoteDetails = clone(mockQuoteDetails);
      state.quotationForm = clone(mockQuoteForm);
      state.boxStructure = clone(mockQuoteBoxStructure);
      state.accessories = clone(mockQuoteAccessories);
    },

    getQuoteDetailsSuccess(state, action: PayloadAction<Quote>) {
      state.detailLoading = false;
      state.selectedQuote = action.payload;
      state.quoteDetails = clone(mockQuoteDetails);
      state.quotationForm = clone(mockQuoteForm);
      state.boxStructure = clone(mockQuoteBoxStructure);
      state.accessories = clone(mockQuoteAccessories);
    },

    getQuoteDetailsFailed(state, action: PayloadAction<string>) {
      state.detailLoading = false;
      state.error = action.payload;
    },

    clearQuoteDetails(state) {
      state.selectedQuote = null;
      state.quoteDetails = null;
      state.quotationForm = null;
      state.boxStructure = null;
      state.accessories = null;
      state.detailLoading = false;
    },

    updateQuotationFormField(
      state,
      action: PayloadAction<{
        field: keyof QuoteQuotationForm;
        value: string | boolean;
      }>
    ) {
      if (!state.quotationForm) {
        state.quotationForm = clone(mockQuoteForm);
      }

      const { field, value } = action.payload;
      state.quotationForm[field] = value as never;
    },

    updateBoxStructure(
      state,
      action: PayloadAction<{
        key: keyof QuoteBoxStructure;
        value: Partial<QuoteFrontWall> | Partial<QuoteOptionItem>;
      }>
    ) {
      if (!state.boxStructure) {
        state.boxStructure = clone(mockQuoteBoxStructure);
      }

      const { key, value } = action.payload;

      if (key === "frontWall") {
        state.boxStructure.frontWall = {
          ...state.boxStructure.frontWall,
          ...(value as Partial<QuoteFrontWall>),
        };
        return;
      }

      state.boxStructure[key] = {
        ...state.boxStructure[key],
        ...(value as Partial<QuoteOptionItem>),
      };
    },

    updateAccessories(state, action: PayloadAction<QuoteAccessoriesUpdate>) {
      const accessories = ensureAccessories(state);
      const { section, key, value } = action.payload;
      const group = accessories[section] as Record<string, QuoteOptionItem>;

      group[key] = {
        ...group[key],
        ...value,
      };
    },

    addAccessoryTableRow(
      state,
      action: PayloadAction<QuoteAccessoryTableKey>
    ) {
      ensureAccessories(state).tables[action.payload].push(
        emptyAccessoryTableRow()
      );
    },

    setQuotesSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
    },

    setQuotesStatusFilter(state, action: PayloadAction<QuoteStatusFilter[]>) {
      state.statusFilter = action.payload;
    },

    createQuoteFromForm(state, action: PayloadAction<AddOrderForm>) {
      const form = action.payload;
      const nextIndex = state.list.length + 1;
      const today = new Date().toISOString().slice(0, 10);
      const validUntil = new Date();
      validUntil.setMonth(validUntil.getMonth() + 1);

      state.list.unshift({
        id: String(Date.now()),
        quoteNumber: `Q-${8090 + nextIndex}`,
        createdAt: today,
        customerNameKey:
          CUSTOMER_OPTIONS.find((item) => item.value === form.customerName)
            ?.labelKey || form.customerName,
        projectTypeKey:
          BOX_TYPE_OPTIONS.find((item) => item.value === form.boxType)
            ?.labelKey || "QUOTES.MOCK.PROJECT.MARCH_10",
        revisionNumber: 1,
        totalValue: 0,
        status: QuoteStatus.DRAFT,
        validUntil: validUntil.toISOString().slice(0, 10),
        descriptionKey: "QUOTES.MOCK.DESCRIPTION.Q_8091",
      });
    },
  },
});

export const { actions: quotesActions, reducer } = quotesSlice;
